import {Inject, Injectable} from '@angular/core';
import {CxEvent, LanguageService, Page, User} from '@spartacus/core';
import {BehaviorSubject, combineLatest, delay, Observable} from 'rxjs';
import {DcpDataService} from '../dcp-data.service';
import {EddlAdobeTrackingData, EddlCollectionState} from './models/eddl-adobe-tracking-data';
import {PageEvent, PageLayoutService, ProductDetailsPageEvent} from '@spartacus/storefront';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {CountrySelectorService} from '../../country-selector.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {distinctUntilChanged, filter, first, map, switchMap} from 'rxjs/operators';
import {DcpFormatDataService} from '../dcp-format-data.service';
import {DOCUMENT} from '@angular/common';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {NavigationStart, Router} from '@angular/router';
import {EddlAfterInitializationEvent} from './events/eddl-after-initialization-event';
import {NavigationComponentService} from '@app/services/navigation-component.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {EddlPageMergedAbstract} from '@app/services/analytics/EDDL/events/eddl-page-merged.abstract';

const EVENT_NAME: string = 'pageView';
const SECTION_HOMEPAGE: string = 'homepage';
const DOMAIN_REGEX: string = '/^(\\/+|\\/+)$/g';

@UntilDestroy()

@Injectable({
  providedIn: 'root'
})
export class DcpEddlPageViewService extends DcpDataService<EddlAdobeTrackingData> {

  private lastPageEvent: PageEvent = undefined;
  private hasBeenInitializedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(DOCUMENT) public documentObject: Document,
    protected activeCartService: DcpActiveCartService,
    protected authService: DcpAuthService,
    protected countrySelectorService: CountrySelectorService,
    protected languageService: LanguageService,
    protected pageLayoutService: PageLayoutService,
    protected formatDataService: DcpFormatDataService,
    protected userFacade: UserAccountFacade,
    protected router: Router,
    protected navigationService: NavigationComponentService,
  ) {
    super();
    this.routerSubscribe();
  }

  fillData(
    event: EddlPageMergedAbstract,
    data?: EddlAdobeTrackingData,
    isUserLoggedIn?: boolean,
    language?: string,
    page?: Page,
    user?: User,
    extra?: any,
  ): EddlAdobeTrackingData {
    const url: string = this.formatDataService.formatUrl(this.documentObject.defaultView.location.href);
    return {
      event: EVENT_NAME,
      page: {
        name: this.getNameFromUrl(event.navigation.url),
        detail: page.description,
        section: this.navigationService.getSectionFromUrl(event.navigation.url),
        language,
        country: user ? this.countrySelectorService.getCountryByUser(user).isocode : this.countrySelectorService.getCurrentCountry().isocode,
        url,
        template: page.template,
        referrer: sessionStorage.getItem('prevLocation') ? sessionStorage.getItem('prevLocation') : '/',
      },
      user: {
        loginStatus: isUserLoggedIn ? 'yes' : 'no',
        scimUserId: isUserLoggedIn && user?.scimUserId ? user.scimUserId : ''
      },
    };
  }

  getData(event: CxEvent): Observable<EddlAdobeTrackingData> | EddlAdobeTrackingData {
    if (event instanceof EddlAfterInitializationEvent) {
      this.hasBeenInitializedSubject.next(true);
    }

    if (!(event instanceof PageEvent)) {
      return undefined;
    }

    if (!this.filterByUrl(event)) {
      return undefined;
    }

    if (this.documentObject.defaultView?.location?.pathname?.split('/')?.slice(-1)?.[0] === 'cart') {
      return combineLatest([this.hasBeenInitializedSubject, this.activeCartService.getActiveCart()]).pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(([initialized, cart]) => Object.keys(cart).length > 0),
        filter(Boolean),
        switchMap(([initialized, cart]) =>
          combineLatest([
            this.authService.isUserLoggedIn(),
            this.languageService.getActive(),
            this.pageLayoutService.page$,
            this.userFacade.get(),
          ]).pipe(
            untilDestroyed(this),
            map(([isLoggedIn, language, page, user]) => {
              (event as EddlPageMergedAbstract).collectToken = cart.guid;
              (event as EddlPageMergedAbstract).collectState = EddlCollectionState.START;
              this.lastPageEvent = event;
              return this.fillData(event, undefined, isLoggedIn, language, page, user);
            })
          )
        )
      );

    }

    return this.hasBeenInitializedSubject.pipe(
      untilDestroyed(this),
      delay(1000),
      distinctUntilChanged(),
      filter(Boolean),
      filter(() => this.documentObject.defaultView?.location?.pathname?.split('/')?.slice(-1)?.[0] !== 'cart'),
      first(),
      switchMap(() =>
        combineLatest([
          this.authService.isUserLoggedIn(),
          this.languageService.getActive(),
          this.pageLayoutService.page$,
          this.userFacade.get(),
        ]).pipe(
          first(),
          distinctUntilChanged(),
          untilDestroyed(this),
          map(([isLoggedIn, language, page, user]) => {
            if (event instanceof ProductDetailsPageEvent) {
              (event as EddlPageMergedAbstract).collectToken = user?.uid ?? event?.code;
              (event as EddlPageMergedAbstract).collectState = EddlCollectionState.FINISH;
            }
            this.lastPageEvent = event;
            return this.fillData(event, undefined, isLoggedIn, language, page, user);
          })
        )
      )
    );
  }

  protected routerSubscribe() {
    this.router.events.pipe(untilDestroyed(this)).subscribe(event => {
      if (event instanceof NavigationStart && event.id !== 1) {
        sessionStorage.setItem('prevLocation', window.location.href);
      }
    });
  }

  protected filterByUrl(event: PageEvent) {
    return event && event.navigation.url !== this.lastPageEvent?.navigation.url;
  }

  protected getNameFromUrl(url: string): string {
    if (typeof url !== 'string') {
      throw new TypeError(`Expected a string for 'url', but got ${typeof url}`);
    }
    const relativePath = url.split('?')?.[0]?.replace(DOMAIN_REGEX, '')?.split('/')?.slice(2)?.join('/');
    return relativePath ? `/${relativePath}` : SECTION_HOMEPAGE;
  }
}
